import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Input, Space, Row, Col, Statistic,
    Select, Typography, Tabs, Avatar, Progress, DatePicker, Dropdown, message
} from 'antd';
import {
    SearchOutlined, DollarOutlined, UserOutlined, EyeOutlined,
    TeamOutlined, CrownOutlined, WalletOutlined, ReloadOutlined,
    RiseOutlined, CheckCircleOutlined, ClockCircleOutlined,
    DownloadOutlined, FileExcelOutlined, CalendarOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import citiesService from '../../common/services/cities.service';
import dayjs from 'dayjs';
import XLSX from 'xlsx-js-style';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const RevenuesListPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [revenues, setRevenues] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(undefined);
    const [stats, setStats] = useState({
        totalAffiliates: 0,
        totalAmbassadors: 0,
        totalRevenueAffiliates: 0,
        totalRevenueAmbassadors: 0,
        totalPaidAffiliates: 0,
        totalPaidAmbassadors: 0,
        totalPendingAffiliates: 0,
        totalPendingAmbassadors: 0
    });

    useEffect(() => {
        const loadCities = async () => {
            try {
                const response = await citiesService.getAll();
                if (response.success) {
                    setCities((response.data || []).filter(c => c.isActive));
                }
            } catch (error) {
                console.error('Erreur chargement villes:', error);
            }
        };
        loadCities();
    }, []);

    const fetchRevenues = useCallback(async () => {
        setLoading(true);
        try {
            const params = { type: activeTab !== 'all' ? activeTab : undefined };

            // Ajouter les filtres de période
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].startOf('day').toISOString();
                params.endDate = dateRange[1].endOf('day').toISOString();
            }

            // Filtre ville
            if (selectedCity) {
                params.city = selectedCity;
            }

            const response = await affiliatesService.getRevenueSummaries(params);
            if (response.success) {
                setRevenues(response.data || []);
                if (response.stats) {
                    setStats(response.stats);
                }
            }
        } catch (error) {
            console.error('Erreur chargement revenus:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, dateRange, selectedCity]);

    useEffect(() => {
        fetchRevenues();
    }, [fetchRevenues]);

    const handleViewDetails = (record) => {
        navigate(`/dashboard/revenues/${record.type}/${record._id}`);
    };

    const filteredRevenues = revenues.filter(r => {
        if (!searchText) return true;
        const search = searchText.toLowerCase().replace(/\s/g, '');
        const name = `${r.firstName || ''} ${r.lastName || ''}`.toLowerCase();
        const phone = (r.phone || '').toLowerCase().replace(/\s/g, '');
        return name.includes(search) || phone.includes(search);
    }).sort((a, b) => (b.driversCount || 0) - (a.driversCount || 0));

    const getTypeTag = (type) => {
        if (type === 'affiliate') {
            return <Tag color="blue" icon={<TeamOutlined />}>Affilié</Tag>;
        }
        return <Tag color="purple" icon={<CrownOutlined />}>Ambassadeur</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        const dataToExport = filteredRevenues.filter(r => activeTab === 'all' || r.type === activeTab);
        return dataToExport.map(r => ({
            'Nom': `${r.firstName || ''} ${r.lastName || ''}`.trim(),
            'Téléphone': r.phone || '',
            'Type': r.type === 'affiliate' ? 'Affilié' : 'Ambassadeur',
            'Chauffeurs recrutés': r.driversCount || 0,
            'Revenus total (F)': r.totalRevenue || 0,
            'Payé (F)': r.paidAmount || 0,
            'En attente (F)': r.pendingAmount || 0,
            'Progression (%)': r.totalRevenue > 0 ? Math.round((r.paidAmount || 0) / r.totalRevenue * 100) : 0,
            'Date inscription': r.createdAt ? dayjs(r.createdAt).format('DD/MM/YYYY') : ''
        }));
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            // Construire le titre selon le filtre actif
            let titleLine = 'Liste des revenus des partenaires';
            if (activeTab === 'affiliate') {
                titleLine = 'Liste des revenus des affiliés';
            } else if (activeTab === 'ambassador') {
                titleLine = 'Liste des revenus des ambassadeurs';
            }

            // Ligne de période
            let periodLine = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodLine = `Période: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
            } else {
                periodLine = `Exporté le: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headers = Object.keys(data[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '', // Ligne vide
                headers.join(';'),
                ...data.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `revenus_${activeTab}_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel (XLS)
    const exportToExcel = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            // Styles
            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "1890FF" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            // Construire le titre selon le filtre actif (en MAJUSCULES)
            let titleText = 'LISTE DES REVENUS DES PARTENAIRES';
            if (activeTab === 'affiliate') {
                titleText = 'LISTE DES REVENUS DES AFFILIÉS';
            } else if (activeTab === 'ambassador') {
                titleText = 'LISTE DES REVENUS DES AMBASSADEURS';
            }

            const periodText = dateRange && dateRange[0] && dateRange[1]
                ? `PÉRIODE: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`
                : `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm').toUpperCase()}`;

            // Créer les lignes d'en-tête
            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                [] // Ligne vide
            ];

            // Créer le worksheet avec les en-têtes d'abord
            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            // Préparer les données avec les en-têtes de colonnes
            const headers = Object.keys(data[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...data.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            // Ajouter les données à partir de la ligne 4 (index 3)
            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Revenus');

            // Ajuster la largeur des colonnes
            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            // Fusionner les cellules pour le titre (première ligne)
            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }, // Titre
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }  // Période
            ];

            // Hauteur des lignes
            worksheet['!rows'] = [
                { hpt: 25 }, // Titre
                { hpt: 20 }, // Période
                { hpt: 15 }, // Ligne vide
                { hpt: 22 }  // En-têtes
            ];

            const filename = `revenus_${activeTab}_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    // Menu d'export
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            width: 280,
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={45}
                        icon={record.type === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                        style={{
                            backgroundColor: record.type === 'affiliate' ? '#1890ff' : '#722ed1',
                            flexShrink: 0
                        }}
                    />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ color: '#888', fontSize: 12 }}>{record.phone}</div>
                        {getTypeTag(record.type)}
                    </div>
                </div>
            )
        },
        {
            title: 'Ville',
            dataIndex: 'cityName',
            key: 'cityName',
            width: 110,
            render: (cityName) => <span>{cityName || '-'}</span>
        },
        {
            title: 'Chauffeurs recrutés',
            dataIndex: 'driversCount',
            key: 'driversCount',
            width: 160,
            align: 'center',
            render: (count, record) => (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                        {count || 0}
                    </div>
                    {record.type === 'ambassador' && (record.directDriversCount > 0 || record.indirectDriversCount > 0) ? (
                        <div style={{ fontSize: 10, marginTop: 2 }}>
                            <span style={{ color: '#9333ea' }}>{record.directDriversCount || 0} dir.</span>
                            {' + '}
                            <span style={{ color: '#f97316' }}>{record.indirectDriversCount || 0} indir.</span>
                        </div>
                    ) : (
                        <div style={{ fontSize: 11, color: '#888' }}>chauffeurs</div>
                    )}
                </div>
            )
        },
        {
            title: 'Revenus total',
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            width: 150,
            sorter: (a, b) => (a.totalRevenue || 0) - (b.totalRevenue || 0),
            render: (amount, record) => (
                <div>
                    <div style={{
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: record.type === 'affiliate' ? '#3f8600' : '#722ed1'
                    }}>
                        {(amount || 0).toLocaleString()} F
                    </div>
                </div>
            )
        },
        {
            title: 'Payé',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            width: 130,
            render: (amount) => (
                <Text style={{ color: '#52c41a' }}>
                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                    {(amount || 0).toLocaleString()} F
                </Text>
            )
        },
        {
            title: 'En attente',
            dataIndex: 'pendingAmount',
            key: 'pendingAmount',
            width: 130,
            render: (amount) => (
                <Text style={{ color: amount > 0 ? '#faad14' : '#888' }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {(amount || 0).toLocaleString()} F
                </Text>
            )
        },
        {
            title: 'Progression',
            key: 'progress',
            width: 150,
            render: (_, record) => {
                const total = record.totalRevenue || 0;
                const paid = record.paidAmount || 0;
                const percent = total > 0 ? Math.round((paid / total) * 100) : 0;
                return (
                    <div style={{ width: 120 }}>
                        <Progress
                            percent={percent}
                            size="small"
                            strokeColor={percent === 100 ? '#52c41a' : '#1890ff'}
                            format={() => `${percent}%`}
                        />
                        <div style={{ fontSize: 10, color: '#888', textAlign: 'center' }}>
                            payé
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Inscrit le',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (date) => dayjs(date).format('DD/MM/YYYY')
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record)}
                >
                    Détails
                </Button>
            )
        }
    ];

    return (
        <div className="revenues-page">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <DollarOutlined style={{ marginRight: 12 }} />
                        Revenus des Partenaires
                    </Title>
                    <Text type="secondary">
                        Vue d'ensemble des revenus des affiliés et ambassadeurs
                    </Text>
                </div>
                <Space>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchRevenues}
                        loading={loading}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Statistiques globales */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><TeamOutlined style={{ marginRight: 8 }} />Affiliés</span>}
                            value={stats.totalAffiliates}
                            valueStyle={{ color: '#1890ff' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Total revenus: <strong style={{ color: '#3f8600' }}>{(stats.totalRevenueAffiliates || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><CrownOutlined style={{ marginRight: 8 }} />Ambassadeurs</span>}
                            value={stats.totalAmbassadors}
                            valueStyle={{ color: '#722ed1' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Total revenus: <strong style={{ color: '#722ed1' }}>{(stats.totalRevenueAmbassadors || 0).toLocaleString()} F</strong>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><CheckCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />Total payé</span>}
                            value={(stats.totalPaidAffiliates || 0) + (stats.totalPaidAmbassadors || 0)}
                            suffix="F"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><ClockCircleOutlined style={{ marginRight: 8, color: '#faad14' }} />En attente</span>}
                            value={(stats.totalPendingAffiliates || 0) + (stats.totalPendingAmbassadors || 0)}
                            suffix="F"
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filtres et recherche */}
            <Card style={{ marginBottom: 16 }}>
                <Space wrap size="middle">
                    <Input
                        placeholder="Rechercher par nom ou téléphone..."
                        prefix={<SearchOutlined />}
                        style={{ width: 280 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                    <Select
                        placeholder="Toutes les villes"
                        allowClear
                        style={{ width: 180 }}
                        value={selectedCity}
                        onChange={(value) => setSelectedCity(value)}
                    >
                        {cities.map(city => (
                            <Option key={city._id} value={city._id}>{city.name}</Option>
                        ))}
                    </Select>
                    <RangePicker
                        placeholder={['Date début', 'Date fin']}
                        format="DD/MM/YYYY"
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates)}
                        allowClear
                        style={{ width: 280 }}
                        presets={[
                            { label: 'Aujourd\'hui', value: [dayjs(), dayjs()] },
                            { label: 'Cette semaine', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                            { label: 'Ce mois', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                            { label: 'Mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                            { label: '3 derniers mois', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs()] },
                            { label: 'Cette année', value: [dayjs().startOf('year'), dayjs()] }
                        ]}
                    />
                    {dateRange && (
                        <Tag color="blue" closable onClose={() => setDateRange(null)}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {dateRange[0]?.format('DD/MM/YYYY')} - {dateRange[1]?.format('DD/MM/YYYY')}
                        </Tag>
                    )}
                </Space>
            </Card>

            {/* Onglets et tableau */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={<span><WalletOutlined /> Tous ({revenues.length})</span>}
                        key="all"
                    />
                    <TabPane
                        tab={<span><TeamOutlined style={{ color: '#1890ff' }} /> Affiliés ({revenues.filter(r => r.type === 'affiliate').length})</span>}
                        key="affiliate"
                    />
                    <TabPane
                        tab={<span><CrownOutlined style={{ color: '#722ed1' }} /> Ambassadeurs ({revenues.filter(r => r.type === 'ambassador').length})</span>}
                        key="ambassador"
                    />
                </Tabs>

                <Table
                    columns={columns}
                    dataSource={filteredRevenues.filter(r => activeTab === 'all' || r.type === activeTab)}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `${total} bénéficiaire(s)`,
                        defaultPageSize: 20
                    }}
                    scroll={{ x: 1200 }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default RevenuesListPage;
